import { tagLabel } from '@/features/deal-review/deal-lead-labels'
import type { DealLeadDetail } from '@/types/api'

type ScreeningRun = NonNullable<DealLeadDetail['latestScreeningRun']>
type CriterionResult = ScreeningRun['criterionResults'][number]

// `evidence` is generated as `Record<string, never>`, so it is read as unknown behind guards.

export type CriterionEvidence =
  | { kind: 'quotation'; quotation: string }
  | {
      kind: 'rule'
      rule: string
      field: string
      required: string
      found: string
    }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null
}

function describe(value: unknown): string {
  if (value === null || value === undefined) return 'not stated'
  if (Array.isArray(value)) {
    return value.length === 0 ? 'none' : value.map(describe).join(', ')
  }
  if (typeof value === 'string') return tagLabel(value)
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return 'not stated'
}

export function readCriterionEvidence(
  value: unknown
): CriterionEvidence | null {
  if (!isRecord(value)) return null

  const rule = readString(value.rule)
  if (rule === null) return null

  if (rule === 'MODEL') {
    const quotation = readString(value.quotation)
    return quotation === null ? null : { kind: 'quotation', quotation }
  }

  const field = readString(value.field)
  if (field === null) return null

  return {
    kind: 'rule',
    rule,
    field,
    required: describeRequirement(rule, value),
    found: describeFound(rule, value.actualValue),
  }
}

// An empty TAG_DENY list means no excluded tag applied, not that no sector was read.
function describeFound(rule: string, actual: unknown): string {
  if (rule === 'TAG_DENY' && Array.isArray(actual) && actual.length === 0) {
    return 'no excluded tag'
  }
  return describe(actual)
}

function describeRequirement(
  rule: string,
  value: Record<string, unknown>
): string {
  if ('denyValues' in value) {
    return `none of ${describe(value.denyValues)}`
  }
  if ('maxValue' in value) {
    return `at most ${describe(value.maxValue)}`
  }
  if ('minValue' in value) {
    return `at least ${describe(value.minValue)}`
  }
  if ('mustEqual' in value) {
    return describe(value.mustEqual)
  }
  return rule
}

// Undecided ones only when nothing failed: after a failure the rest are recorded as not judged.
export function cardedCriteria(run: ScreeningRun): CriterionResult[] {
  const failed = run.criterionResults.filter(
    (result) => result.outcome === 'FAIL'
  )
  if (failed.length > 0) return failed

  return run.criterionResults.filter((result) => result.outcome === 'UNKNOWN')
}

export function openQuestionsTitle(run: ScreeningRun): string | null {
  const carded = cardedCriteria(run)
  if (carded.length === 0) return null
  if (carded[0].outcome === 'FAIL') return 'What failed'
  return carded.length === 1 ? 'Open question' : 'Open questions'
}
